import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    ButtonTheme,
    NumberBox,
    NumberBoxComponentSpacing,
    ShowIf,
} from '@apply/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleExclamation, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import {
    useDeleteOwner,
    useOwnersQuery,
    useSaveOwner,
    useSaveOwnerPercentages,
} from '../queries/businessProfileQueries';
import { Owner, OwnerPercentage, ownerDisplayName } from '../types';
import { SummaryCard, SummaryRow } from '../components/SummaryCard';
import { OwnerModal } from '../components/OwnerModal';
import { calculateOwnershipTotal } from '../utils/calculateOwnershipTotal'
import { StepErrorBanner } from '../components/StepErrorBanner';
import { StepStatus } from '../components/StepStatus';
import { useActionError } from '../hooks/useActionError';
import { RegisterStep } from '../wizard/stepApi';

interface OwnersStepProps {
    businessProfileId: number | null;
    activeEntityId: number | null;
    entityName: string;
    onRegister: RegisterStep;
}

export const OwnersStep: React.FC<OwnersStepProps> = ({
    businessProfileId,
    activeEntityId,
    entityName,
    onRegister,
}) => {
    const entityId = activeEntityId ?? 0;
    const { data: owners, isLoading, isError, error, refetch } = useOwnersQuery(entityId);
    const { mutateAsync: saveOwner } = useSaveOwner(businessProfileId);
    const { mutateAsync: deleteOwner } = useDeleteOwner(entityId, businessProfileId);
    const { mutateAsync: savePercentages } = useSaveOwnerPercentages(businessProfileId);
    const rowAction = useActionError();

    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<Owner | undefined>(undefined);

    const [percents, setPercents] = useState<Record<number, number | null>>({});

    useEffect(() => {
        if (!owners) {
            return;
        }
        setPercents((prior) => {
            const next: Record<number, number | null> = {};
            for (const owner of owners) {
                // Keep any value the user has touched; otherwise take the server's
                next[owner.id] = owner.id in prior ? prior[owner.id] : owner.ownershipPercent;
            }
            return next;
        });
    }, [owners]);

    const effectiveOwners: Owner[] = useMemo(
        () =>
            (owners ?? []).map((owner) => ({
                ...owner,
                ownershipPercent: owner.id in percents ? percents[owner.id] : owner.ownershipPercent,
            })),
        [owners, percents],
    );

    const allocation = useMemo(() => calculateOwnershipTotal(effectiveOwners), [effectiveOwners]);

    const pending: OwnerPercentage[] = useMemo(
        () =>
            (owners ?? [])
                .filter((owner) => owner.id in percents && percents[owner.id] !== owner.ownershipPercent)
                .map((owner) => ({ ownerId: owner.id, ownershipPercent: percents[owner.id] })),
        [owners, percents],
    );

    const save = useCallback(async () => {
        if (pending.length === 0) {
            return;
        }
        await savePercentages({ entityId, percentages: pending });
        setPercents({});
    }, [pending, entityId, savePercentages]);

    useEffect(() => {
        onRegister({ save, canAdvance: allocation.canAdvance, isDirty: pending.length > 0 });
    }, [save, allocation.canAdvance, pending.length, onRegister]);

    const openAdd = () => {
        setEditing(undefined);
        setModalOpen(true);
    };

    const openEdit = (owner: Owner) => {
        setEditing(owner);
        setModalOpen(true);
    };

    const onPercentChange = (owner: Owner, percent: number) => {
        setPercents((prior) => ({ ...prior, [owner.id]: percent }));
    };

    const rows: SummaryRow[] = effectiveOwners.map((owner) => ({
        key: String(owner.id),
        title: ownerDisplayName(owner),
        trailing: (
            <div className='bp-summaryCard__percent'>
                <span className='bp-summaryCard__percentLabel'>Ownership:</span>
                <NumberBox
                    id={`bpOwnerPercent_${owner.id}`}
                    value={owner.ownershipPercent ?? undefined}
                    onChange={(value) => onPercentChange(owner, value)}
                    format='#0'
                    min={0}
                    max={100}
                    width='90px'
                    liveUpdate={true}
                    componentSpacing={NumberBoxComponentSpacing.None}
                />
            </div>
        ),
        actions: [
            { label: 'Edit Details', onClick: () => openEdit(owner) },
            {
                label: 'Delete Owner',
                destructive: true,
                onClick: () => void rowAction.run(() => deleteOwner(owner.id)),
            },
        ],
    }));

    const banner = () => {
        switch (allocation.status) {
            case 'none':
                return {
                    className: 'bp-allocation bp-allocation--error',
                    icon: faCircleExclamation,
                    text: `Please add the ownership percentage to ${
                        rows.length === 1 ? rows[0].title : 'each owner'
                    }.`,
                };
            case 'partial':
                return {
                    className: 'bp-allocation bp-allocation--warning',
                    icon: faTriangleExclamation,
                    text: `Ownership must equal 100% before continuing to Other Officers. You have allocated ${allocation.total}%. Please continue adding owners.`,
                };
            case 'over':
                return {
                    className: 'bp-allocation bp-allocation--error',
                    icon: faCircleExclamation,
                    text: `Ownership totals ${allocation.total}%. Please reduce it to 100%.`,
                };
            case 'complete':
                return {
                    className: 'bp-allocation bp-allocation--success',
                    icon: faCircleCheck,
                    text: `You have allocated 100% ownership to ${entityName}.`,
                };
        }
    };

    if (isLoading || isError) {
        return (
            <StepStatus
                isLoading={isLoading}
                isError={isError}
                error={error}
                onRetry={() => void refetch()}
            />
        );
    }

    const state = banner();

    return (
        <div className='bp-step'>
            {rowAction.error != null && (
                <StepErrorBanner
                    error={rowAction.error}
                    onDismiss={rowAction.clear}
                />
            )}
            <ShowIf showIf={rows.length > 0}>
                <div className={state.className}>
                    <span className='bp-allocation__percent'>{allocation.total}%</span>
                    <FontAwesomeIcon
                        className='bp-allocation__icon'
                        icon={state.icon}
                    />
                    <span>{state.text}</span>
                </div>
            </ShowIf>

            <SummaryCard
                id='bpOwners'
                heading='Owners'
                rows={rows}
                emptyText='Add the owner(s) of the borrower entity by type: individual person, entity, or a group of people (if no single person in the group owns more than 20%).'
            />

            <div style={{ marginTop: 24 }}>
                <Button
                    id='bpAddOwnerButton'
                    dataTestid='bpAddOwnerButton'
                    theme={rows.length === 0 ? ButtonTheme.Primary : ButtonTheme.Secondary}
                    width='100%'
                    maxWidth='346px'
                    handleClick={openAdd}
                >
                    {rows.length === 0 ? 'Add Owner' : 'Add Another Owner'}
                </Button>
            </div>

            <OwnerModal
                isOpen={modalOpen}
                owner={editing}
                ownedEntityId={entityId}
                onSave={async (owner) => {
                    await saveOwner(owner);
                }}
                onClose={() => setModalOpen(false)}
            />
        </div>
    );
};
